import {
  CellClassParams,
  CellStyle,
  ColDef,
  ColumnApi,
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ISetFilterParams,
  MenuItemDef,
  Module,
  RowClickedEvent,
  RowDoubleClickedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { SetFilterModel } from '@ag-grid-enterprise/set-filter'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  getFilterOnCellContextMenu,
  HeliosColumnTypes,
  heliosDefaultToolPanels,
  heliosMultiRowSelectionConfig,
  heliosServerSideRowConfig,
  NumberFilterOptionsWithAbsolute,
  NumberFilterOptionsWithoutEquals,
  setFilter,
  TextFilterOptionsWithContainsIn,
} from '@helios/ag-grid-config'
import {
  BulkTransactionModalInput,
  BulkTransactionModalMode,
  BulkTransactionsModalService,
  CreateTransactionsModalService,
  TransactionModalInput,
  TransactionModalMode,
} from '@helios/create-transactions-modal'
import {
  AdvancedOptionsParams,
  FilterModalEvent,
  FilterModalService,
  FilterModel,
} from '@helios/filter-modal'
import {
  getGridState,
  HighlightingService,
  mergeBaseColDefsWithColumnState,
} from '@helios/generic-components'
import {
  convertFilters,
  countFilters,
  handleColumnVisibilityChange,
  HeliosServerSideRequest,
  toHeliosRequest,
} from '@helios/server-side-util'
import {
  ExternalFileFilter,
  ExternalFileLinkedPanel,
  GridState,
  RootState,
  securityMappingAction,
  SecurityMappingFilter,
  SecurityMappingLinkedPanel,
  selectActiveSavedViewForGrid,
  selectActiveSession,
  selectActiveSessionReconDate,
  selectIsHplAccount,
  selectIsInternalUser,
  selectPositionsSessionCacheStatus,
  selectSavedViewExistsForGridKey,
  selectUserPolicy,
  SessionCacheStatus,
  showRawDataAction,
  userHasCommentaryAccess,
} from '@helios/state-management'
import {
  createFilterGroupsToolPanel,
  DensityLevel,
  FilterGroupsToolPanelParams,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { downloadExcelFile, isNullOrEmpty } from '@helios/utils'
import {
  AssetIdListValidator,
  AssetIdValidator,
  IntegerIdValidator,
} from '@helios/validators'
import {
  CommentaryCountsService,
  CommentaryLiveAccountsService,
  CommentaryEntityRef,
  CommentaryEntitySearchService,
  CommentaryFollowService,
  CommentaryGridSelectionService,
  CommentCountCellRenderer,
  commentCountRendererKey,
  wireCommentaryGrid,
  wireCommentaryCounts,
  refilterOnThreadChanges,
  activeSessionAccountIds,
  getCommentaryColumnIfAccessible,
  getCommentaryContextMenuItems,
  COMMENTARY_COLUMN_ID,
  assetCounts,
} from '@helios/comments'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
import { composeFieldPath } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { refreshSetValuesForAllSetFilterColumns } from 'libs/filter-modal/src/lib/utils/column-filter-utils'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
// eslint-disable-next-line
import {
  AgGridSortModel,
  convertSortModel,
  SortModel,
} from 'libs/server-side-util/src/lib/convert-sort'
import _, { debounce } from 'lodash-es'
import { BehaviorSubject, Observable, of, Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs/operators'
import { PositionsKey } from '../../panel-key'
import { CustomNoRowsOverlayComponent } from '../custom-overlay/custom-no-rows-overlay.component'
import { getExchangeFromPositionMatch } from '../exchange-positions/exchange-positions-util'
import { PositionExchangeErrorComponent } from '../modals/position-exchange-error/position-exchange-error.component'
import {
  ExchangeFromPositionMatchReq,
  ExchangeFromPositionMatchRes,
  PositionMatch,
} from '../model/position-match.model'
import {
  ExportExcelRequest,
  Position,
  PositionAvailableRestrictedColumns,
  PositionsActionPermissions,
  PositionsSetValues,
} from '../model/positions.model'
import {
  AdvancedFilterContext,
  AdvanceOptionsContext,
  PositionsAvailableActions,
  PositionsRequestContext,
  PositionsService,
} from '../services/positions.service'
import { positionCommentary } from './positions-commentary'

/**
 * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
 * preferences for this grid.
 */
export const PositionsMainGridKey = `${PositionsKey}-MainGrid`

const positionPath = composeFieldPath([] as Position[])
const securityFilterCol = 'secIdentifier'
const SEC_MAPPING_ERROR =
  'This feature requires selecting exactly one CW_ONLY position and exactly one EXTERNAL_ONLY position.'
@Component({
  selector: 'hs-positions-grid',
  templateUrl: './positions-grid.component.html',
  styleUrls: ['./positions-grid.component.scss'],
  providers: [HighlightingService, FilterModalService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PositionsGridComponent implements OnInit, OnDestroy {
  private readonly destroySubject$ = new Subject<void>()
  /** The session this grid was created for; a session switch replaces the grid. */
  private sessionAccountIds?: readonly number[]
  private cacheComplete = false
  /**
   * Rows/Positions selected in positions ag-grid.
   */
  selectedPositions: Position[] | null = null

  filterCount = 0
  private gridState?: GridState

  callingExchange = false

  isInternalUser = false

  hasSecurityMappingPermissions = false
  hasExternalFileViewerPermissions = false
  hasAccessCommentary = false

  availableActionsPermissions: Promise<PositionsActionPermissions> =
    this.getAvailableActionsPermissions()

  actionPermissionsCache: PositionsActionPermissions = {
    canTransferOutAllPositionsOneSided: false,
    canTransferOutAllPositionsDirect: false,
    canFilterTransactionByAssetId: false,
    canFilterTransactionByCurrency: false,
    canExchange: false,
    canTransferPosition: false,
    canCreateSecurityMapping: false,
  }

  sortByAbsoluteValueToggle = new FormControl(false)
  clearFiltersOnAccountLoadToggle = new FormControl(true)
  extToExtComparisonToggle = new FormControl(false)
  entryDateCashBalanceToggle = new FormControl(false)

  @Output() extToExtComparisonModeChanged = new EventEmitter<boolean>()

  @Input()
  set rowsUpdated(positionIds: string[] | null) {
    if (positionIds != null && positionIds.length > 0) {
      this.refreshServerSideStore()
    }
  }

  /**
   * @internal
   */
  readonly refreshServerSideStore = debounce(
    () => {
      this.resetPositionsSetValues()
      this.gridApi?.api.refreshServerSideStore({ purge: false })
    },
    100,
    { maxWait: 250 },
  )

  private _focusedSecurityId: number | undefined | null
  @Input()
  set focusedSecurityId(securityId: number | undefined | null) {
    this.setFocusedSecurityId(securityId)
  }

  /** emitted when a user selects a position in the grid */
  @Output()
  positionSelected = new EventEmitter<Position[] | null>()

  /** emitted when a user selects a position to filter transactions by in the context menu */
  @Output()
  securityIdFilterSelected = new EventEmitter<number>()
  /** emitted when a user selects a position to filter transactions by in the context menu */
  @Output()
  currencyFilterSelected = new EventEmitter<string>()

  resetFiltersEnabled = false

  positionsSetValues$: Observable<PositionsSetValues> =
    this.getPositionsSetValues()

  readonly gridModules: Module[] = [
    FiltersToolPanelModule,
    ServerSideRowModelModule,
  ]

  /**
   * public for testing reasons
   *
   * @internal
   */
  gridApi?: AgGridApi
  quickSearchAssetIds = new FormControl('', AssetIdListValidator)
  includeClosedPositionsToggle = new FormControl(false)
  showOnlyDifferencesToggle = new FormControl(false)
  showOnlyIncorrectToggle = new FormControl(false)
  sessionReconDate?: Date

  applyHPL = this.store.select(selectIsHplAccount).pipe(take(1)).toPromise()
  /** @internal */
  gridOptions$ = this.createGridOptions()

  availableRestrictedColumns = this.getAvailableRestrictedColumns()

  propertiesTitle: string[] = []

  advancedOptions: AdvancedOptionsParams<
    AdvancedFilterContext,
    AdvanceOptionsContext
  > = {
    context: {},
  }

  isIncorrectOnlyFilterOn$ = new BehaviorSubject<boolean | undefined>(true)

  /** @internal */
  readonly ONLY_DIFFERENCES_FILTER = {
    type: 'notEqual',
    filterType: 'number',
    filter: '0',
  }

  /**
   * @internal
   */
  readonly ONLY_INCORRECT_FILTER: SetFilterModel = {
    filterType: 'set',
    values: ['INCORRECT'],
  }

  /**
   * @internal
   */
  get filterGroupsToolPanelParams(): FilterGroupsToolPanelParams<AdvancedFilterContext> {
    return {
      gridKey: PositionsMainGridKey,
      getExternalFilters: () => this.advancedOptions.context,
      setExternalFilters: context =>
        this.applyExternalFilters({
          onlyDifferences: undefined,
          onlyIncorrect: undefined,
          context,
        }),
      cacheLoaded: of(true),
    }
  }

  constructor(
    private readonly positionsService: PositionsService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly store: Store<RootState>,
    private readonly createTransactionsModalService: CreateTransactionsModalService,
    private readonly bulkTransactionsModalService: BulkTransactionsModalService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
    // @internal
    public highlightService: HighlightingService,
    public filterModalService: FilterModalService,
    private readonly preferences: UserPreferencesService,
    private readonly commentaryCountsService: CommentaryCountsService,
    private readonly commentaryLiveAccounts: CommentaryLiveAccountsService,
    private readonly commentaryFollowService: CommentaryFollowService,
    private readonly entitySearchService: CommentaryEntitySearchService,
    private readonly gridSelectionService: CommentaryGridSelectionService,
  ) {}

  ngOnInit() {
    this.filterModalService.isClearFiltersEnabled = false
    this.filterModalService.isResetFiltersEnabled = true
    this.filterModalService.setValidators({
      acctId: IntegerIdValidator,
      secIdentifier: AssetIdValidator,
    })
    this.sortByAbsoluteValueToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
      this.gridApi?.api.onFilterChanged()
    })
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
    this.extToExtComparisonToggle.valueChanges.subscribe((value: boolean) => {
      this.updateGridConfig()
      // update the flag and a new object is passed to trigger lower grid changes
      this.triggerChangeInSubPanels(value)
    })

    this.quickSearchAssetIds.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroySubject$),
      )
      .subscribe(() => this.updateQuickSearchAssetIds())

    this.store.select(selectActiveSessionReconDate).subscribe(reconDate => {
      this.sessionReconDate = reconDate
    })

    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        this.sessionAccountIds = session?.loadedAccountIds
      })

    this.store
      .select(selectPositionsSessionCacheStatus)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(status => {
        this.cacheComplete =
          status === SessionCacheStatus.DONE ||
          status === SessionCacheStatus.POST_LOAD_DONE
      })

    this.store.select(selectIsInternalUser).subscribe(internalUser => {
      this.isInternalUser = internalUser
    })

    this.availableActionsPermissions.then(permissions => {
      this.actionPermissionsCache = permissions
    })

    this.store
      .select(selectUserPolicy)
      .pipe(take(1))
      .subscribe(policies => {
        this.hasSecurityMappingPermissions =
          policies?.accessSecurityMappingPanel ?? false
        this.hasExternalFileViewerPermissions =
          policies?.accessExternalFileViewerPanel ?? false
      })

    this.store
      .select(selectUserPolicy)
      .pipe(
        takeUntil(this.destroySubject$),
        map(policy => userHasCommentaryAccess(policy)),
        // keep listening only until access is first granted, then complete
        takeWhile(access => !access, true),
      )
      .subscribe(access => {
        this.hasAccessCommentary = access
        if (access) {
          this.wireCommentary()
        }
      })
  }

  /**
   * DLITE-3849 — wire this grid into the commentary drawer (follow-a-chip row
   * focus + entitySearch/pickEntities sources). positions owns `asset`;
   * idColId is `secIdentifier` (the positions grid's row-node id is
   * `positionId`, so revealGridRow filters on the sec-id column). Called at
   * most once — the policy pipe in ngOnInit completes after its first
   * granting emission. Torn down via destroySubject$.
   */
  private wireCommentary() {
    wireCommentaryGrid({
      follow: this.commentaryFollowService,
      search: this.entitySearchService,
      selection: this.gridSelectionService,
      panelKey: PositionsKey,
      entityType: 'asset',
      idColId: 'secIdentifier',
      getApi: () => this.gridApi?.api,
      hasAccess: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      loadedRefs: () => this.collectLoadedAssetRefs(),
      selectedRefs: () => this.collectSelectedAssetRefs(),
      hasRowNodeId: false,
      toast: this.toastService,
      sessionAccountIds: () => this.sessionAccountIds,
      // The grid is created while the cache is still partial and, unlike the
      // transaction grids, its requests don't wait for it to finish.
      isComplete: () => this.cacheComplete,
    })
  }

  private collectLoadedAssetRefs(): CommentaryEntityRef[] {
    const seen = new Set<string>()
    const refs: CommentaryEntityRef[] = []
    this.gridApi?.api.forEachNode(node => {
      const id = (node.data as Position)?.secIdentifier
      if (id != null && !seen.has(id)) {
        seen.add(id)
        refs.push({ type: 'asset', id })
      }
    })
    return refs
  }

  private collectSelectedAssetRefs(): CommentaryEntityRef[] {
    const seen = new Set<string>()
    const refs: CommentaryEntityRef[] = []
    ;(this.gridApi?.api.getSelectedNodes() ?? []).forEach(node => {
      const id = (node.data as Position)?.secIdentifier
      if (id != null && !seen.has(id)) {
        seen.add(id)
        refs.push({ type: 'asset', id })
      }
    })
    return refs
  }
  triggerChangeInSubPanels(value: boolean) {
    if (!this.selectedPositions) return

    const valuePositions = JSON.parse(
      JSON.stringify(this.selectedPositions),
    ) as Position[]
    valuePositions.forEach(
      position => (position.isExternalToExternalModeSelected = value),
    )
    this.selectedPositions = valuePositions
    this.positionSelected.next(valuePositions)
  }

  ngOnDestroy() {
    this.selectedPositions = null
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
    this.filterModalService.destroy()
  }

  handleSelectionChanged() {
    const value = this.gridApi?.api.getSelectedRows() as Position[]

    if (value) {
      value.forEach(
        position =>
          (position.isExternalToExternalModeSelected =
            this.extToExtComparisonToggle.value),
      )
    }
    this.selectedPositions = value
    this.positionSelected.next(value)
  }

  handleRowClicked(clickedEvent: RowClickedEvent) {
    const value = this.gridApi?.api.getSelectedRows() as Position[]
    const keyEvent = clickedEvent.event as PointerEvent
    if (keyEvent?.altKey) {
      this.gridApi?.api.setFilterModel(null)
      this.gridApi?.api.onFilterChanged()
      this.advancedOptions = {
        context: {},
      }
      this.quickSearchAssetIds.reset('')
      this.includeClosedPositionsToggle.setValue(false)
      this.entryDateCashBalanceToggle.setValue(false)
      this.showOnlyDifferencesToggle.setValue(false)
      this.setOnlyIncorrectFilter(false)
      this.quickSearchAssetIds.setValue(clickedEvent.data.secIdentifier)
    }
    if (value) {
      value.forEach(
        position =>
          (position.isExternalToExternalModeSelected =
            this.extToExtComparisonToggle.value),
      )
    }

    this.selectedPositions = value
    this.positionSelected.next(value)
  }

  /**
   * Transfer Out all the positions in the filter, not only the rows displayed in the grid.
   */
  transferOutAllPositions(type: string) {
    const tableFilters = this.gridApi?.api.getFilterModel()
    let filters
    if (tableFilters) filters = convertFilters(tableFilters)
    const fields: string[] = []
    this.gridApi?.columnApi.getAllDisplayedColumns().forEach(col => {
      const colField = col.getColId()
      if (colField === COMMENTARY_COLUMN_ID) return
      fields.push(colField)
    })
    const sortModel = this.gridApi?.api.getSortModel()
    const sort = convertSortModel(
      sortModel as AgGridSortModel<Position>[],
      { keys: [], fields: [] },
      this.sortByAbsoluteValueToggle.value,
    )

    const request: HeliosServerSideRequest<Position, PositionsRequestContext> =
      {
        startRow: 0,
        endRow: 9999999,
        fields: fields,
        filter: filters as FilterModel,
        context: {
          filterContext: { ...this.advancedOptions.context },
        } as PositionsRequestContext,
        sort: sort as unknown as SortModel<Position>[],
        group: { keys: [], fields: [] },
      }

    if (type == 'oneSided') {
      this.bulkTransactionsModalService.toggleSecurityTransferOutModal({
        modalMode: BulkTransactionModalMode.CreateFromPosition,
        transferAllPositionsRequest: request,
      })
    } else if (type == 'direct') {
      this.bulkTransactionsModalService.toggleDirectTransferModal({
        modalMode: BulkTransactionModalMode.CreateCWTran,
        transferAllPositionsRequest: request,
      })
    }
  }

  /** Right-click menu */
  private getContextMenuItems(params: GetContextMenuItemsParams) {
    const data = params?.node?.data ?? ({} as Position)
    const rowsSelected = params?.api?.getSelectedNodes().map(t => t.data) ?? []

    const [smDisabled, smtooltip] =
      this.validateSecurityMappingMenuItem(rowsSelected)

    const menuItems: (string | MenuItemDef)[] = [
      {
        name: 'Filter Transactions by Asset', //Adds a security filter to the Transaction List panel
        action: () => {
          this.filterSecurityIdTransactionListAction(data)

          // update this panel's filter — key off the numeric secId (matching the
          // transaction emit above) rather than secIdentifier/Asset ID, which is
          // empty for external users and would produce an `equals: undefined` filter.
          this.setSecurityIdFilter(data.secId)
        },
      },
      {
        name: 'Filter Transactions by Currency', //Adds a currency filter to the Transaction List panel
        action: () => {
          this.filterCurrencyTransactionListAction(data)
        },
        disabled: data.posTypeCode !== 'Cash',
        tooltip:
          data.posTypeCode !== 'Cash'
            ? 'This option is only available for cash positions'
            : undefined,
      },
      {
        name: 'Exchange', //Exchange a cwan and external position
        action: () => {
          if (this.selectedPositions)
            this.performExchange(
              this.selectedPositions[0],
              this.selectedPositions[1],
            )
        },
        disabled: this.selectedPositions?.length !== 2,
        tooltip:
          this.selectedPositions?.length !== 2
            ? 'Can only exchange exactly 2 securities'
            : undefined,
      },
      {
        name: 'Transfer Position',
        disabled: this.disableTransferPositionOption(rowsSelected),
        tooltip: this.disableTransferPositionOption(rowsSelected)
          ? 'Two or more positions have same Asset ID and Account ID'
          : undefined,
        subMenu: [
          {
            name: 'Direct Transfer',
            action: () =>
              this.selectedPositions?.length === 1
                ? this.toggleTransferModal(data, 'directTransfer', false)
                : this.toggleTransferModal(
                    rowsSelected,
                    'directTransfer',
                    true,
                  ),
          },
          {
            name: 'Security Transfer Out',
            action: () =>
              this.selectedPositions?.length === 1
                ? this.toggleTransferModal(data, 'securityTransfer', false)
                : this.toggleTransferModal(
                    rowsSelected,
                    'securityTransfer',
                    true,
                  ),
          },
        ],
      },
      'separator',
      {
        name: 'Find in External File Viewer',
        disabled:
          !this.hasExternalFileViewerPermissions || data.extOriginId == null,
        tooltip: !this.hasExternalFileViewerPermissions
          ? 'Feature not available due to lack of permission'
          : 'View in raw data file',
        action: () =>
          data.extOriginId != null
            ? this.store.dispatch(
                showRawDataAction({
                  id: data.extOriginId,
                  accountId: data.acctId,
                  reconDate: data.matchDate,
                  filterText: data.secIdentifier,
                  panelName: ExternalFileLinkedPanel.POSITION,
                } as ExternalFileFilter),
              )
            : '',
      },
      {
        name: 'Create Security Mapping',
        disabled:
          !this.hasSecurityMappingPermissions || (smDisabled as boolean),
        tooltip: !this.hasSecurityMappingPermissions
          ? 'Feature not available due to lack of permission'
          : (smtooltip as string),
        action: () => this.openSecurityMappingPanel(rowsSelected),
      },
      ...getFilterOnCellContextMenu(params),
      'separator',
      ...this.highlightService.createHighlightMenuItems(params),
      ...getCommentaryContextMenuItems(
        this.store,
        this.hasAccessCommentary,
        positionCommentary,
        data,
      ),
    ]

    return this.filterItemsBasedOnAvailableActions(menuItems)
  }

  private filterItemsBasedOnAvailableActions(items: (string | MenuItemDef)[]) {
    if (this.isInternalUser) {
      return items
    }
    return items.filter(item => {
      if (typeof item === 'string') {
        return true
      }

      switch (item.name) {
        case 'Filter Transactions by Asset':
          return this.actionPermissionsCache.canFilterTransactionByAssetId

        case 'Filter Transactions by Currency':
          return this.actionPermissionsCache.canFilterTransactionByCurrency

        case 'Exchange':
          return this.actionPermissionsCache.canExchange

        case 'Transfer Position':
          return this.actionPermissionsCache.canTransferPosition

        case 'Create Security Mapping':
          return this.actionPermissionsCache.canCreateSecurityMapping

        default:
          return true
      }
    })
  }

  private handleRowDoubleClicked(event: RowDoubleClickedEvent) {
    this.filterSecurityIdTransactionListAction(event.node.data)
  }

  //#region GridConfig
  private async createGridOptions(): Promise<GridOptions> {
    return {
      ...heliosServerSideRowConfig,
      columnDefs: await this.createBaseColumnDefs(),
      components: {
        [commentCountRendererKey]: CommentCountCellRenderer,
      },
      defaultColDef: {
        cellStyle: (params: CellClassParams) =>
          this.highlightService.getCellStyle(
            params?.node?.id ?? '',
            params?.colDef?.field ?? '',
          ) as CellStyle,
      },
      rowSelection: 'multiple',
      serverSideDatasource: this.createDatasource(),
      suppressRowClickSelection: false,
      ...heliosMultiRowSelectionConfig,
      getContextMenuItems: params => this.getContextMenuItems(params),
      onColumnVisible: params =>
        handleColumnVisibilityChange(params, 'Positions'),
      onGridReady: event => this.onGridReady(event),
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Show Closed Positions',
              formControl: this.includeClosedPositionsToggle,
            },
            {
              label: 'Sort by Absolute Value',
              formControl: this.sortByAbsoluteValueToggle,
            },
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
            {
              label: 'External To External Comparison',
              formControl: this.extToExtComparisonToggle,
            },
            {
              label: 'Entry Date Cash Balances',
              formControl: this.entryDateCashBalanceToggle,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
        createFilterGroupsToolPanel<AdvancedFilterContext>(
          this.filterGroupsToolPanelParams,
        ),
      ]),
      getRowClass: params => [
        this.highlightService.getRowClass(params?.node?.id ?? ''),
      ],
      getRowNodeId: ({ positionId }: Position) => positionId,
      onRowDoubleClicked: event => this.handleRowDoubleClicked(event),
      onRowClicked: event => this.handleRowClicked(event),
      onSelectionChanged: () => this.handleSelectionChanged(),
      suppressCopyRowsToClipboard: true,
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onFilterChanged: (params: FilterChangedEvent) =>
        this.onFilterChanged(params),
      noRowsOverlayComponentFramework: CustomNoRowsOverlayComponent,
      noRowsOverlayComponentParams: {
        noRowsMessageFunc: () =>
          this.isIncorrectOnlyFilterOn$.value
            ? `No Incorrect Positions`
            : `No Data`,
      },
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
    }
  }

  /**
   * Exports the transactions to Excel same as visible in the CW table
   */
  exportPositionsToExcel() {
    const tableFilters = this.gridApi?.api.getFilterModel()
    let filters
    if (tableFilters) filters = convertFilters(tableFilters)
    const fields: string[] = []
    const colHeaderNames: string[] = []
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    this.gridApi?.columnApi.getAllDisplayedColumns().forEach(col => {
      const colField = col.getColId()
      if (colField === COMMENTARY_COLUMN_ID) return
      fields.push(colField)
      const headerName = col.getUserProvidedColDef()?.headerName
      if (headerName) colHeaderNames.push(headerName)
    })

    const request: ExportExcelRequest = {
      fields: fields,
      headers: colHeaderNames,
      filters: filters as FilterModel,
      userTimeZone: userTimeZone,
      context: {
        filterContext: { ...this.advancedOptions.context },
      } as PositionsRequestContext,
    }

    this.positionsService.getPositionsExportExcel(request).subscribe(
      response => {
        downloadExcelFile(response, 'Positions')
        this.toastService.success(
          "Export to Excel completed successfully. The exported file is available in your browser's Downloads",
          {
            closeButton: true,
            timeOut: 3000,
          },
        )
      },
      () => {
        this.toastService.error(
          'Export to Excel failed. Please try again.',
          '',
          {
            closeButton: true,
            timeOut: 3000,
            disableTimeOut: false,
          },
        )
      },
    )
    this.toastService.warning(
      'Export to Excel is in progress. It may take more than 30 seconds. Please wait.',
      {
        closeButton: true,
        timeOut: 3000,
      },
      'In Progress',
    )
  }

  /**
   * @internal
   */
  async createBaseColumnDefs(): Promise<ColDef[]> {
    const hplColumnType = (await this.applyHPL)
      ? HeliosColumnTypes.HPLNumber
      : NfColumnTypes.Number

    const baseColDefs: ColDef[] = [
      ...(await getCommentaryColumnIfAccessible(
        this.store,
        positionCommentary,
        () => this.hasAccessCommentary,
        this.commentaryCountsService,
        assetCounts,
      )),
      {
        ...this.getSetValuesFilterParams('acctId'),
        field: positionPath('acctId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
        valueFormatter: undefined,
        sortable: true,
        hide: true,
      },
      {
        ...this.getSetValuesFilterParams('acctName'),
        field: positionPath('accountName'),
        headerName: 'Account Name',
        sortable: true,
      },
      {
        ...this.getSetValuesFilterParams('secId'),
        field: positionPath('secId'),
        headerName: 'Security ID',
        type: NfColumnTypes.Identifier,
        valueFormatter: undefined,
        sortable: true,
      },
      {
        field: positionPath('secCusip'),
        headerName: 'Sec Cusip',
        sortable: true,
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: positionPath('secIsin'),
        headerName: 'Sec Isin',
        sortable: true,
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: positionPath('secFigi'),
        headerName: 'Sec Figi',
        sortable: true,
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: positionPath('secSedol'),
        headerName: 'Sec Sedol',
        sortable: true,
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        ...this.getSetValuesFilterParams('matchStatus'),
        field: positionPath('matchStatus'),
        headerName: 'Match Status',
        sortable: true,
      },
      {
        ...this.getSetValuesFilterParams('posTypeCode'),
        field: positionPath('posTypeCode'),
        headerName: 'Position Type',
        sortable: true,
      },
      {
        field: positionPath('secIdentifier'),
        headerName: 'Asset ID',
        colId: securityFilterCol,
        // DLITE-3849: explicit text filter so the commentary "follow" flow's
        // setFilterModel({ secIdentifier: equals }) fallback works (positions'
        // row-node id is positionId, not secIdentifier, so the follow path
        // always uses the filter rather than getRowNode).
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: positionPath('cwanIdentifier'),
        headerName: 'Asset Identifier',
        initialHide: false,
        sortable: true,
        width: 120,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: positionPath('matchDate'),
        headerName: 'Match Date',
        type: NfColumnTypes.Date,
        sortable: true,
      },
      {
        field: positionPath('units'),
        headerName: 'Units',
        type: hplColumnType,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        field: positionPath('unitDifference'),
        headerName: 'Unit Difference',
        type: hplColumnType,
        sortable: true,
        cellClass: params => {
          return Math.abs(Math.trunc(params.value * 1000) / 1000) >= 0.005
            ? ' text-danger'
            : ''
        },
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        field: positionPath('description'),
        headerName: 'Description',
        sortable: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: positionPath('extPosId'),
        headerName: 'Ext Position ID',
        type: NfColumnTypes.Identifier,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },
      },
      {
        ...this.getSetValuesFilterParams('secTypeCode'),
        field: positionPath('secTypeCode'),
        headerName: 'Security Type',
        sortable: true,
      },
      {
        ...this.getSetValuesFilterParams('extPosSource'),
        field: positionPath('extPosSource'),
        headerName: 'External Source',
        sortable: true,
      },
      {
        ...this.getSetValuesFilterParams('extPosSourceType'),
        field: positionPath('extPosSourceType'),
        headerName: 'External Source Type',
        sortable: true,
      },
      {
        field: positionPath('cwanUnits'),
        type: hplColumnType,
        headerName: 'CWAN Current Units',
        initialHide: false,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        field: positionPath('cwanOriginalUnits'),
        type: hplColumnType,
        headerName: 'CWAN Original Units',
        initialHide: false,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },

      {
        field: positionPath('externalUnits'),
        headerName: 'External Units',
        type: hplColumnType,
        initialHide: false,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        field: positionPath('status'),
        headerName: 'Status',
        valueFormatter: this.getStatusName,
        hide: true,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          refreshValuesOnOpen: true,
          values: ['INCORRECT', 'NO_ERROR'],
          valueFormatter: this.getStatusName,
        } as ISetFilterParams,
      },
      {
        field: positionPath('currencyCode'),
        ...this.getSetValuesFilterParams('currencyCode'),
        headerName: 'Currency Code',
        sortable: true,
      },
      {
        field: positionPath('normModelId'),
        headerName: 'Norm Model ID',
        type: NfColumnTypes.Identifier,
        initialHide: false,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },
      },
      {
        ...this.getSetValuesFilterParams('inventoryType'),
        field: positionPath('inventoryType'),
        headerName: 'Inventory Type',
        sortable: true,
      },
      {
        ...this.getSetValuesFilterParams('collateralBroker'),
        field: positionPath('collateralBroker'),
        headerName: 'Collateral Broker',
        sortable: true,
      },
    ]

    const savedViewExists = await this.store
      .pipe(
        selectSavedViewExistsForGridKey(PositionsMainGridKey),
        takeUntil(this.destroySubject$),
        take(1),
      )
      .toPromise()

    if (!savedViewExists) {
      return baseColDefs
    }

    const savedColumnState = await this.store
      .pipe(
        takeUntil(this.destroySubject$),
        selectActiveSavedViewForGrid(PositionsMainGridKey),
        take(1),
        map(savedView => savedView.savedView.columnState),
      )
      .toPromise()

    let colDefs = mergeBaseColDefsWithColumnState(baseColDefs, savedColumnState)

    const availableColumns = await this.availableRestrictedColumns
    if (availableColumns) {
      const columnsToHide = availableColumns
        .filter(column => !column.isAvailable)
        .map(column => column.column)

      colDefs = colDefs.map(colDef => {
        if (colDef.colId && columnsToHide.includes(colDef.colId)) {
          return {
            ...colDef,
            hide: true,
            suppressMoveable: true,
            suppressMenu: true,
            suppressColumnsToolPanel: true,
          }
        }
        return colDef
      })
    }

    return colDefs
  }

  //Function to disable Transfer Position Option if any of the positions has the same secIdentifier with the same acctId
  disableTransferPositionOption(data: Position[]): boolean {
    const positionMap = new Map()

    for (const item of data) {
      const key =
        item.acctId +
        '-' +
        item.secIdentifier +
        '-' +
        item.inventoryType +
        '-' +
        item.collateralBroker
      if (positionMap.has(key)) {
        return true
      }
      positionMap.set(key, true)
    }
    return false
  }

  toggleTransferModal(
    data: Position | Position[],
    modalType: 'directTransfer' | 'securityTransfer',
    isBulkTransfer: boolean,
  ) {
    let transInput
    let positionData

    if (isBulkTransfer) {
      positionData = (data as Position[]).map(row =>
        this.buildPositionData(row),
      )
      transInput = {
        modalMode: BulkTransactionModalMode.CreateFromPosition,
        positionData: positionData,
      } as unknown as BulkTransactionModalInput
    } else {
      positionData = this.buildPositionData(data as Position)
      transInput = {
        modalMode: TransactionModalMode.CreateFromPosition,
        positionData: positionData,
      } as unknown as TransactionModalInput
    }

    this.toggleModal(transInput, modalType, isBulkTransfer)
  }

  buildPositionData(data: Position) {
    return {
      sessionReconDate: this.sessionReconDate,
      assetId: data.secIdentifier,
      accountId: data.acctId,
      units: data.cwanOriginalUnits ?? '',
      securityId: data.secId,
      collateralBroker: data.collateralBroker,
      inventoryType: data.inventoryType ?? 'LONG',
    }
  }

  toggleModal(
    transInput: TransactionModalInput | BulkTransactionModalInput,
    modalType: 'directTransfer' | 'securityTransfer',
    isBulkTransfer: boolean,
  ) {
    if (modalType === 'directTransfer') {
      if (isBulkTransfer)
        this.bulkTransactionsModalService.toggleDirectTransferModal(
          transInput as BulkTransactionModalInput,
        )
      else
        this.createTransactionsModalService.toggleDirectTransferModal(
          transInput as TransactionModalInput,
        )
    } else if (modalType === 'securityTransfer') {
      if (isBulkTransfer)
        this.bulkTransactionsModalService.toggleSecurityTransferOutModal(
          transInput as BulkTransactionModalInput,
        )
      else
        this.createTransactionsModalService.toggleSecurityTransferModal(
          transInput as TransactionModalInput,
        )
    }
  }

  getStatusName = (params: ValueFormatterParams) => {
    switch (params.value) {
      case 'INCORRECT':
        return 'Break'
      case 'NO_ERROR':
        return 'No Break'
      default:
        return ''
    }
  }

  private createDatasource(): IServerSideDatasource {
    return {
      getRows: async (params: IServerSideGetRowsParams) => {
        try {
          const request = toHeliosRequest<Position, PositionsRequestContext>(
            params,
            [],
            positionCommentary.mandatoryFields(
              'positionId',
              'secId',
              'extOriginId',
              'currencyCode',
              'posTypeCode',
              'matchStatus',
              'extPosId',
              'matchDate',
              'units',
              'cwanOriginalUnits',
            ),
            {
              filterContext: { ...this.advancedOptions.context },
            } as PositionsRequestContext,
            this.sortByAbsoluteValueToggle.value,
          )

          this.filterCount = countFilters(params.api, null) ?? 0
          this.gridState = getGridState(this.gridApi)
          const positions = await this.positionsService
            .getPositions(request)
            .toPromise()

          const totalRows = (params?.request?.startRow ?? 0) + positions.length
          params.success({
            rowData: positions,
            rowCount:
              totalRows < (params?.request?.endRow ?? totalRows)
                ? totalRows
                : undefined,
          })
        } catch (e) {
          params.fail()
        }
      },
    }
  }

  getSetValuesFilterParams(key: keyof PositionsSetValues) {
    return {
      filter: 'agSetColumnFilter',
      filterParams: {
        refreshValuesOnOpen: true,
        defaultToNothingSelected: true,
        values: params => {
          this.positionsSetValues$.subscribe(values =>
            params.success(values[key] as string[]),
          )
        },
        closeOnApply: true,
      } as ISetFilterParams,
    }
  }

  updateQuickSearchAssetIds() {
    if (this.quickSearchAssetIds.invalid) {
      return
    }

    const filter = isNullOrEmpty(this.quickSearchAssetIds.value)
      ? undefined
      : {
          type: 'containsIn',
          filterType: 'text',
          filter: this.quickSearchAssetIds.value,
        }

    this.setFilter('secIdentifier', filter)
  }

  //#endregion GridConfig

  /**
   * Filters transaction lists by security Id.
   *
   * Keys off the numeric security id (rather than the Asset ID / secIdentifier)
   * so the focus behavior also works for external users, who do not have the
   * Asset ID column populated.
   *
   * @param position Position
   * @private
   */
  private filterSecurityIdTransactionListAction(position: Position) {
    const { secId } = position
    if (secId != null) {
      if (position.secTypeCode == 'CASH') {
        this.filterCurrencyTransactionListAction(position)
      } else this.securityIdFilterSelected.emit(secId)
    }
  }

  /**
   * Filters transaction lists by currency
   *
   * @param position Position
   * @internal
   */
  filterCurrencyTransactionListAction(position: Position) {
    const { secIdentifier, posTypeCode, currencyCode } = position
    if (
      secIdentifier != null &&
      secIdentifier !== '' &&
      posTypeCode === 'Cash'
    ) {
      this.currencyFilterSelected.emit(currencyCode)
    }
  }

  /**
   * Verify Whether exchange is possible adn if yes Open the same
   *
   * @param position1 position1
   * @param position2 position2
   * @internal
   */
  performExchange(position1: Position, position2: Position) {
    const positionMatchRequest: ExchangeFromPositionMatchReq = {
      firstPosMatch: this.positionToMatch(position1),
      secondPosMatch: this.positionToMatch(position2),
    }
    const exchangeFromPositionMatchRes = getExchangeFromPositionMatch(
      positionMatchRequest.firstPosMatch,
      positionMatchRequest.secondPosMatch,
    )
    this.verifyAndDoExchange(
      exchangeFromPositionMatchRes,
      position1.secIdentifier == exchangeFromPositionMatchRes.outCusip
        ? position1.collateralBroker
        : position2.collateralBroker,
    )
  }

  verifyAndDoExchange(
    exchangeFromPositionMatchRes: ExchangeFromPositionMatchRes,
    collateralBroker?: string,
  ) {
    if (exchangeFromPositionMatchRes.canExchange) {
      this.createTransactionsModalService.toggleDirectTransferModal({
        modalMode: TransactionModalMode.CreateFromPosition,
        positionData: {
          oldCusip: exchangeFromPositionMatchRes.outCusip,
          newCusip: exchangeFromPositionMatchRes.inCusip,
          oldCwanIdentifier: exchangeFromPositionMatchRes.outCwanIdentifier,
          newCwanIdentifier: exchangeFromPositionMatchRes.inCwanIdentifier,
          rate: exchangeFromPositionMatchRes.rate,
          units: exchangeFromPositionMatchRes.units,
          collateralBroker: collateralBroker,
        },
      })
    } else {
      this.modalService.open(PositionExchangeErrorComponent, {
        keyboard: false,
      })
    }
  }
  positionToMatch(position: Position): PositionMatch {
    return {
      matchStatus: position.matchStatus,
      cusip: position.secCusip ?? position.secIdentifier,
      cwanUnits: position.cwanUnits,
      externalUnits: position.externalUnits,
      securityId: position.secId,
      cwanIdentifier: position.cwanIdentifier,
    }
  }

  /**
   * When a new session is loaded or updates are made to the data we should get the set values again.
   * @private
   */
  private resetPositionsSetValues() {
    this.positionsSetValues$ = this.getPositionsSetValues()
  }

  private getPositionsSetValues() {
    return this.positionsService
      .getPositionsSetValues()
      .pipe(take(1), shareReplay(1))
  }

  private async updateAvailableColumns(columnApi: ColumnApi): Promise<void> {
    const columns = await this.availableRestrictedColumns
    const columnsToHide = columns
      .filter(column => !column.isAvailable)
      .map(column => column.column)

    const updatedColumns = columnsToHide.map(column => {
      return {
        colId: column,
        hide: true,
      }
    })

    columnApi.applyColumnState?.({
      state: updatedColumns,
      applyOrder: true,
    })
  }

  /** @internal */
  async onGridReady(event: GridReadyEvent) {
    this.gridApi = event

    wireCommentaryCounts<Position>({
      changes$: this.commentaryCountsService.changes$,
      getApi: () => event.api,
      hasAccessForLoad: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      accountIdOf: row => row?.acctId,
      loadCounts: ids =>
        this.commentaryCountsService.ensureEntityCountsLoaded(ids),
      live: { accounts: this.commentaryLiveAccounts, surface: 'entity' },
    })
    refilterOnThreadChanges<Position>(() => event.api, this.destroySubject$, {
      accountIdOf: row => row?.acctId,
      queriedAccountIds: activeSessionAccountIds(
        this.store,
        this.destroySubject$,
      ),
    })

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )

    enableHeliosCopyBehavior(event.api)
    enableHeliosMultiRowDragSelection(event.api)

    //apply any initial filters AFTER the saved view is restored
    this.setSecurityIdFilter(this._focusedSecurityId)
    this.filterModalService
      .registerGrid(
        event.api,
        PositionsKey,
        PositionsMainGridKey,
        [COMMENTARY_COLUMN_ID],
        undefined,
        () => ({
          context: {
            includeClosedPositions: this.includeClosedPositionsToggle.value,
            calcCashUsingEntryDate: this.entryDateCashBalanceToggle.value,
          },
          onlyDifferences: this.showOnlyDifferencesToggle.value,
          onlyIncorrect: this.showOnlyIncorrectToggle.value,
        }),
      )
      .pipe(
        takeUntil(this.destroySubject$),
        map(
          x =>
            x as FilterModalEvent<
              AdvancedOptionsParams<
                AdvancedFilterContext,
                AdvanceOptionsContext
              >
            >,
        ),
      )
      .subscribe(e => {
        this.applyExternalFilters(e.payload)
      })

    this.filterModalService.onResetFilters
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.resetFilters()
      })

    this.includeClosedPositionsToggle.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroySubject$))
      .subscribe(areClosedPositionsIncluded => {
        this.setClosedPositionsOnFormChange(areClosedPositionsIncluded)
      })

    this.entryDateCashBalanceToggle.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroySubject$))
      .subscribe(isEntryDateCashBalance => {
        this.setEntryDateCashBalanceOnFormChange(isEntryDateCashBalance)
      })

    this.showOnlyDifferencesToggle.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroySubject$))
      .subscribe(shouldShowOnlyDifferences => {
        this.setOnlyDifferencesFilter(shouldShowOnlyDifferences)
      })

    this.showOnlyIncorrectToggle.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(shouldShowOnlyIncorrect => {
        this.setOnlyIncorrectFilter(shouldShowOnlyIncorrect)
      })

    /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
    const agGridStateSaved = await this.savedViewService.manageSavedView(
      event,
      PositionsMainGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )

    this.applyGridConfig(agGridStateSaved)
    if (this.clearFiltersOnAccountLoadToggle.value) {
      this.setOnlyIncorrectFilter(true)
    }
    this.updateResetFiltersEnabledButton(event.api)
    await this.updateAvailableColumns(event.columnApi)
    refreshSetValuesForAllSetFilterColumns(event)
  }

  applyExternalFilters({
    onlyDifferences,
    onlyIncorrect,
    context,
  }: AdvancedOptionsParams<
    AdvancedFilterContext,
    AdvanceOptionsContext
  >): void {
    const unitDiffFilter = this.gridApi?.api
      .getFilterInstance('unitDifference')
      ?.getModel()

    if (context != null && Object.keys(context).length > 0) {
      this.setOnlyDifferencesFilter(onlyDifferences)
    } else {
      const unitDiffNotEqualFilter =
        unitDiffFilter != null &&
        unitDiffFilter.type === 'notEqual' &&
        unitDiffFilter.filter === '0'
      this.setOnlyDifferencesFilter(unitDiffNotEqualFilter)
    }

    if (onlyIncorrect !== undefined) {
      this.setOnlyIncorrectFilter(onlyIncorrect)
    }
    if (context?.includeClosedPositions !== undefined) {
      this.setClosedPositionsOnContext(context?.includeClosedPositions)
    }
    if (context?.calcCashUsingEntryDate !== undefined) {
      this.setEntryDateCashBalanceOnContext(context?.calcCashUsingEntryDate)
    }
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['sortByAbsolute'] = this.sortByAbsoluteValueToggle.value
    panelSettings['includeClosedPositions'] =
      this.includeClosedPositionsToggle.value
    panelSettings['entryDateCashBalance'] =
      this.entryDateCashBalanceToggle.value
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    panelSettings['extToExtComparison'] = this.extToExtComparisonToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('sortByAbsolute' in panelSettings)
        this.sortByAbsoluteValueToggle.patchValue(
          Boolean(panelSettings.sortByAbsolute),
          { emitEvent: false },
        )

      if ('clearFiltersOnAccountLoad' in panelSettings)
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )

      if ('extToExtComparison' in panelSettings)
        this.extToExtComparisonToggle.patchValue(
          Boolean(panelSettings.extToExtComparison),
          { emitEvent: false },
        )

      if ('includeClosedPositions' in panelSettings) {
        this.includeClosedPositionsToggle.patchValue(
          Boolean(panelSettings.includeClosedPositions),
          { emitEvent: false },
        )
        this.setClosedPositionsAdvancedOption(
          Boolean(panelSettings.includeClosedPositions),
        )
      }

      if ('entryDateCashBalance' in panelSettings) {
        this.entryDateCashBalanceToggle.patchValue(
          Boolean(panelSettings.entryDateCashBalance),
          { emitEvent: false },
        )
        this.setEntryDateCashBalanceAdvancedOption(
          Boolean(panelSettings.entryDateCashBalance),
        )
      }
    }
  }

  /**
   * @internal
   */
  async setFilter(columnName: keyof Position, filter: unknown) {
    await setFilter<Position>(columnName, filter, this.gridApi?.api)
  }

  /** @internal */
  setClosedPositionsOnContext(includeClosedPositions?: boolean) {
    this.includeClosedPositionsToggle.setValue(includeClosedPositions, {
      emitEvent: false,
    })
    this.setClosedPositionsAdvancedOption(includeClosedPositions)
    this.gridApi?.api.onFilterChanged()
  }

  setEntryDateCashBalanceOnContext(isEntryDateCashBalance?: boolean) {
    this.entryDateCashBalanceToggle.setValue(isEntryDateCashBalance, {
      emitEvent: false,
    })
    this.setEntryDateCashBalanceAdvancedOption(isEntryDateCashBalance)
    this.gridApi?.api.onFilterChanged()
  }

  setClosedPositionsOnFormChange(includeClosedPositions?: boolean) {
    this.setClosedPositionsAdvancedOption(includeClosedPositions)
    this.updateGridConfig()
    this.gridApi?.api.onFilterChanged()
  }

  setEntryDateCashBalanceOnFormChange(isEntryDateCashBalance?: boolean) {
    this.setEntryDateCashBalanceAdvancedOption(isEntryDateCashBalance)
    this.updateGridConfig()
    this.gridApi?.api.onFilterChanged()
  }

  setEntryDateCashBalanceAdvancedOption(isEntryDateCashBalance?: boolean) {
    if (isEntryDateCashBalance) {
      this.advancedOptions.context.calcCashUsingEntryDate =
        isEntryDateCashBalance
    } else {
      delete this.advancedOptions.context.calcCashUsingEntryDate
    }
  }

  setClosedPositionsAdvancedOption(includeClosedPositions?: boolean) {
    if (includeClosedPositions) {
      this.advancedOptions.context.includeClosedPositions =
        includeClosedPositions
    } else {
      delete this.advancedOptions.context.includeClosedPositions
    }
  }

  setOnlyDifferencesFilter(onlyDifferences?: boolean) {
    this.showOnlyDifferencesToggle.setValue(onlyDifferences)
    this.advancedOptions.onlyDifferences = onlyDifferences
    this.setFilter(
      'unitDifference',
      onlyDifferences ? this.ONLY_DIFFERENCES_FILTER : undefined,
    )
  }

  async setOnlyIncorrectFilter(onlyIncorrect?: boolean) {
    this.showOnlyIncorrectToggle.setValue(onlyIncorrect, { emitEvent: false })
    this.advancedOptions.onlyIncorrect = onlyIncorrect
    await this.setFilter(
      'status',
      onlyIncorrect ? this.ONLY_INCORRECT_FILTER : undefined,
    )

    this.isIncorrectOnlyFilterOn$.next(onlyIncorrect)
  }

  private async setSecurityIdFilter(securityId?: number | null) {
    await this.setFilter(
      'secId',
      securityId
        ? {
            filterType: 'set',
            values: [securityId + ''],
          }
        : undefined,
    )
  }

  private onFilterChanged(event: FilterChangedEvent) {
    const hasStatusFilterChanged =
      event.columns?.some(c => c.getColId() === 'status') ?? false
    if (hasStatusFilterChanged) {
      this.onStatusFilterChanged(event.api)
    }
    const hasUnitDifferenceChanged =
      event.columns?.some(c => c.getColId() === 'unitDifference') ?? false
    if (hasUnitDifferenceChanged) {
      this.onUnitDiffFilterChanged(event.api)
    }
    this.onAssetIdFilterChanged(event.api)

    this.updateResetFiltersEnabledButton(event.api)
  }

  private updateResetFiltersEnabledButton(api: GridApi) {
    const filterModels = api.getFilterModel?.() ?? {}
    const activeFilters = Object.keys(filterModels).filter(k => k !== 'status')
    const isDefaultStatusFilter =
      filterModels.status !== undefined &&
      _.isEqual(filterModels.status.values, ['INCORRECT'])
    const isSetToDefaults = isDefaultStatusFilter

    this.resetFiltersEnabled = activeFilters.length > 0 || !isSetToDefaults
  }

  private onStatusFilterChanged(api: GridApi) {
    const model = api.getFilterInstance('status')?.getModel()
    const isOnlyIncorrectShown =
      model?.values.length === 1 && model.values[0] === 'INCORRECT'
    this.showOnlyIncorrectToggle.setValue(isOnlyIncorrectShown, {
      emitEvent: false,
    })
  }

  private onUnitDiffFilterChanged(api: GridApi) {
    const model = api.getFilterInstance('unitDifference')?.getModel()
    const isOnlyDifferenceShown =
      model?.type === this.ONLY_DIFFERENCES_FILTER.type &&
      model?.filter === this.ONLY_DIFFERENCES_FILTER.filter
    this.showOnlyDifferencesToggle.setValue(isOnlyDifferenceShown, {
      emitEvent: false,
    })
  }

  private onAssetIdFilterChanged(api: GridApi) {
    const filterModel = api.getFilterModel?.() ?? {}
    const assetIdValue = filterModel.secIdentifier?.filter
    const filterType = filterModel.secIdentifier?.type
    if (filterType === 'containsIn') {
      this.quickSearchAssetIds.setValue(assetIdValue)
    } else {
      this.quickSearchAssetIds.reset('', { emitEvent: false })
    }
  }

  resetFilters() {
    this.gridApi?.api.setFilterModel(null)
    this.gridApi?.api.onFilterChanged()
    this.advancedOptions = {
      context: this.advancedOptions.context,
    }
    this.quickSearchAssetIds.reset('')
    this.showOnlyDifferencesToggle.setValue(false)
    this.setOnlyIncorrectFilter(true)
  }

  launchFiltersModal() {
    this.filterModalService.launchFiltersModal()
  }

  async setFocusedSecurityId(securityId?: number | null) {
    if (securityId == null) {
      return
    }

    this._focusedSecurityId = securityId
    this.resetFilters()
    await this.setOnlyIncorrectFilter(false)
    await this.setSecurityIdFilter(securityId)
  }

  validateSecurityMappingMenuItem(rowsSelected: Position[]) {
    if (rowsSelected.length == 1) {
      if (rowsSelected[0].matchStatus == 'EXTERNAL_ONLY') {
        return [false, 'Create security mapping']
      }
      return [true, 'Select EXTERNAL_ONLY position to create security mapping']
    } else if (rowsSelected.length == 2) {
      if (
        (rowsSelected[0].matchStatus == 'CW_ONLY' &&
          rowsSelected[1].matchStatus == 'EXTERNAL_ONLY') ||
        (rowsSelected[0].matchStatus == 'EXTERNAL_ONLY' &&
          rowsSelected[1].matchStatus == 'CW_ONLY')
      ) {
        return [false, 'Create security mapping']
      } else {
        return [true, SEC_MAPPING_ERROR]
      }
    }
    return [true, SEC_MAPPING_ERROR]
  }

  openSecurityMappingPanel(positions: Position[]) {
    if (positions.length > 2) {
      return
    }
    let extPosId: number | undefined
    let extPosCusip: string | undefined
    positions.forEach(pos => {
      if (pos.matchStatus == 'CW_ONLY') {
        extPosCusip = pos.secIdentifier
      }
      if (pos.matchStatus == 'EXTERNAL_ONLY') {
        extPosId = pos.extPosId
      }
    })
    this.store.dispatch(
      securityMappingAction({
        extPostionId: extPosId,
        extPositionCusip: extPosCusip,
        panelName: SecurityMappingLinkedPanel.POSITION,
      } as SecurityMappingFilter),
    )
  }

  hasAvailableAction(
    actions: string[],
    actionToCheck: PositionsAvailableActions,
  ): boolean {
    return actions.includes(actionToCheck)
  }

  private async getAvailableActionsPermissions(): Promise<PositionsActionPermissions> {
    const actions = await this.positionsService
      .getPositionsAvailableActions()
      .pipe(take(1))
      .toPromise()

    return {
      canTransferOutAllPositionsOneSided: this.hasAvailableAction(
        actions,
        PositionsAvailableActions.TRANSFER_OUT_ALL_POSITIONS_ONE_SIDED,
      ),
      canTransferOutAllPositionsDirect: this.hasAvailableAction(
        actions,
        PositionsAvailableActions.TRANSFER_OUT_ALL_POSITIONS_DIRECT,
      ),
      canFilterTransactionByAssetId: this.hasAvailableAction(
        actions,
        PositionsAvailableActions.FILTER_TRANSACTIONS_BY_ASSET_ID,
      ),
      canFilterTransactionByCurrency: this.hasAvailableAction(
        actions,
        PositionsAvailableActions.FILTER_TRANSACTIONS_BY_CURRENCY,
      ),
      canExchange: this.hasAvailableAction(
        actions,
        PositionsAvailableActions.EXCHANGE,
      ),
      canTransferPosition: this.hasAvailableAction(
        actions,
        PositionsAvailableActions.TRANSFER_POSITION,
      ),
      canCreateSecurityMapping: this.hasAvailableAction(
        actions,
        PositionsAvailableActions.CREATE_SECURITY_MAPPING,
      ),
    }
  }

  private async getAvailableRestrictedColumns(): Promise<
    PositionAvailableRestrictedColumns[]
  > {
    return this.positionsService
      .getAvailableRestrictedColumns()
      .pipe(
        takeUntil(this.destroySubject$),
        map(columns =>
          columns.map(column => {
            if (column.column === 'metaData') {
              return { column: 'metaData_8', isAvailable: column.isAvailable }
            } else return column
          }),
        ),
      )
      .toPromise()
  }
}
